import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MapPin } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1581262177533-1b1760b87952?crop=entropy&cs=srgb&fm=jpg&q=85";

function RevealLine({ children, delay, className = "" }) {
  return (
    <span className={`block overflow-hidden ${className}`}>
      <motion.span
        className="block"
        initial={{ y: "115%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function KineticHero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  return (
    <section
      ref={ref}
      data-testid="kinetic-hero"
      className="relative flex min-h-screen items-center overflow-hidden pt-28 pb-16"
    >
      <div className="pointer-events-none absolute -left-40 top-1/4 h-[480px] w-[480px] rounded-full bg-[#D4AF37]/[0.05] blur-[120px]" />
      <div className="mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-16 px-6 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <RevealLine delay={0.15}>
            <span className="mb-8 inline-flex items-center gap-2 border border-[#D4AF37]/30 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.3em] text-[#E5C158]">
              <MapPin size={12} /> Paris · Île-de-France · Prestige
            </span>
          </RevealLine>
          <h1 className="font-display text-4xl font-light leading-[1.08] tracking-tight text-[#F5F3EF] sm:text-5xl lg:text-6xl">
            <RevealLine delay={0.3}>Votre bien mérite</RevealLine>
            <RevealLine delay={0.45}>
              une estimation <em className="text-[#D4AF37]">d'exception.</em>
            </RevealLine>
          </h1>
          <RevealLine delay={0.65}>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-stone-400 sm:text-lg">
              La référence de l'estimation immobilière de prestige, à Paris et dans toute
              l'Île-de-France. Une estimation documentée — transactions réelles DVF, comparables
              récents, sources croisées — remise en quelques minutes, affinée par un conseiller dédié.
            </p>
          </RevealLine>
          <RevealLine delay={0.85}>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/estimation" data-testid="hero-cta-estimation" className="btn-gold">
                Estimer mon bien <ArrowRight size={16} />
              </Link>
              <a href="#methode" data-testid="hero-cta-methode" className="btn-ghost">
                Découvrir la méthode
              </a>
            </div>
          </RevealLine>
          <RevealLine delay={1.05}>
            <div className="mt-14 grid max-w-lg grid-cols-3 gap-6 border-t border-stone-800 pt-8">
              <div data-testid="hero-stat-delai">
                <p className="font-display text-2xl text-[#E5C158]">3 min</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">Questionnaire</p>
              </div>
              <div data-testid="hero-stat-sources">
                <p className="font-display text-2xl text-[#E5C158]">4 sources</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">Croisées</p>
              </div>
              <div data-testid="hero-stat-rapport">
                <p className="font-display text-2xl text-[#E5C158]">1 rapport</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">Téléchargeable</p>
              </div>
            </div>
          </RevealLine>
        </div>
        <motion.div
          className="relative hidden lg:col-span-5 lg:block"
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="absolute -inset-4 translate-x-6 translate-y-6 border border-[#D4AF37]/30" />
          <div className="relative h-[560px] overflow-hidden">
            <motion.div
              style={{ y: imgY }}
              className="absolute inset-[-60px] bg-cover bg-center"
            >
              <div
                className="h-full w-full bg-cover bg-center"
                style={{ backgroundImage: `url(${HERO_IMG})` }}
              />
            </motion.div>
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0D]/70 via-transparent to-[#0B0B0D]/20" />
          </div>
          <p className="mt-4 text-right font-mono text-[10px] uppercase tracking-[0.25em] text-stone-600">
            La Butte Montmartre — XVIII<sup>e</sup> arrondissement
          </p>
        </motion.div>
      </div>
    </section>
  );
}
